import { ChevronRight } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { PipelineStage, Customer } from '../../types';

interface PipelineStageHeaderCollapsedProps {
  stage: PipelineStage;
  customers: Customer[];
  stageValue: number;
  hasSearchMatches: boolean;
  shouldDimStage: boolean;
  onToggleCollapse: () => void;
}

/**
 * Collapsed (minimized) stage header - compact vertical layout
 *
 * Design: Minimal information for quick scanning
 * - Count badge at top (primary info)
 * - Stage name horizontal with 2-line wrap
 * - Expand chevron at bottom
 *
 * Width: 48px (set by CSS variable --pipeline-stage-width-collapsed)
 */
export function PipelineStageHeaderCollapsed({
  stage,
  customers,
  hasSearchMatches,
  shouldDimStage,
  onToggleCollapse,
}: PipelineStageHeaderCollapsedProps) {
  // Determine if name needs tooltip (longer than can comfortably display)
  const needsTooltip = stage.name.length > 12;

  // Common header content - extracted to avoid duplication
  const headerContent = (
    <div
      className={`pipeline-collapsed-header w-full ${
        hasSearchMatches ? 'ring-2 ring-white/50' : ''
      } ${shouldDimStage ? 'opacity-60' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onToggleCollapse();
      }}
      role="button"
      tabIndex={0}
      aria-label={`Expand ${stage.name} stage (${customers.length} customers)`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onToggleCollapse();
        }
      }}
    >
      {/* Count badge - primary info */}
      <div className="pipeline-collapsed-badge">
        {customers.length}
      </div>

      {/* Stage name - horizontal, 2-line max with truncation */}
      <span className="pipeline-collapsed-name">
        {stage.name}
      </span>

      {/* Expand chevron */}
      <div className="pipeline-collapsed-expand">
        <ChevronRight className="h-3.5 w-3.5" />
      </div>
    </div>
  );

  // Only wrap in Tooltip if name is long enough to need it
  if (needsTooltip) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          {headerContent}
        </TooltipTrigger>
        <TooltipContent side="right" className="z-50">
          <div className="text-sm">
            <p className="font-medium">{stage.name}</p>
            <p className="text-xs text-muted-foreground">
              {customers.length} customer{customers.length !== 1 ? 's' : ''}
            </p>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  // No tooltip needed for short names
  return headerContent;
}
